import { useTranslation } from '@michelin/central-provider';
import { DealerAutocomplete } from 'components/Autocomplete';
import { Dialog, DialogActions, Grid } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { Button, TextField, useDialogs } from '@michelin/acid-components';
import { useForm, Controller } from 'react-hook-form';
import { NotificationSubscription } from 'Types';
import ListingFilterSelect from '@michelin/acid-components/build/components/ListingFilters/ListingFilterSelect';
import { getOptions, SelectOption } from '@michelin/select-options-provider';
import { Email } from '@material-ui/icons';
import { ClosableDialogTitle } from 'components/ClosableDialog/ClosableDialogTitle';
import { ClosableDialogContent } from 'components/ClosableDialog/ClosableDialogContent';
import { hasEmailFormat } from 'Utils';

interface NotificationSubscriptionsDetailProps {
  onSave: (notification: NotificationSubscription) => void;
  onClose: () => void;
  action: string;
  data?: NotificationSubscription;
}

interface NotificationSubscriptionForm {
  event: string;
  type: string;
  method: string[];
  cc_emails?: string;
}

const NotificationSubscriptionsDetail = (
  props: NotificationSubscriptionsDetailProps,
) => {
  const { t } = useTranslation();
  const { errorDialog, confirmDialog } = useDialogs();

  const {
    handleSubmit, control, setValue, watch, formState,
  } = useForm<NotificationSubscriptionForm>({
    defaultValues: {
      event: props.data?.event || '',
      type: props.data?.type || '',
      method: props.data?.method || [],
      cc_emails: props.data?.cc_emails?.join(',') || '',
    },
  });

  const eventWatch = watch('event');
  const typeWatch = watch('type');
  const methodWatch = watch('method');

  const { isDirty } = formState;

  const [eventsOptions] = useState(
    getOptions('dealer_contact_notifications_events'),
  );
  const [typeOptions, setTypesOption] = useState<SelectOption[]>([]);
  const [methodOptions, setMethodOption] = useState<SelectOption[]>([]);
  const [selectorChange, setSelectorChange] = useState<undefined | string>(
    undefined,
  );

  const title = props.action === 'add'
    ? t('Add a new subscription notification')
    : t('Update an existing subscription notification');

  /// //////////////////////////////////////////////////////////////////
  /// VALIDATE METHODS
  /// //////////////////////////////////////////////////////////////////

  function onDialogClose() {
    if (!isDirty) return props.onClose();
    return confirmDialog(
      t('Do you want to discard those changes?').split('\n'),
      t('You have unsaved changes...'),
      t('Stay here'),
      t('Discard changes'),
      undefined,
      () => props.onClose(),
    );
  }

  const onError = (errors: any) => {
    if (errors.event || errors.type || errors.method) {
      errorDialog(
        t('Please check for empty fields').split('\n'),
        t('Error checking notification subscription data.'),
      );
      return;
    }

    if (errors.cc_emails.type === 'format') {
      errorDialog(
        t('Please check for wrong email format').split('\n'),
        t('Error checking notification subscription data.'),
      );
      return;
    }
    if (errors.cc_emails.type === 'duplicates') {
      errorDialog(
        t('Please check for duplicate email addresses').split('\n'),
        t('Error checking notification subscription data.'),
      );
    }
  };

  const emailToArray = (cc_emails: string): string[] => {
    let emails = cc_emails.replace(/;/g, ',').replace(/\n/g, ',');
    emails = emails.replace(/\s/g, '');
    return emails.split(',').filter((email: string) => email.length > 0);
  };

  function saveNotificationSubscription(dataForm: {
    event: string;
    type: string;
    method: [];
    cc_emails: string;
  }) {
    const data: NotificationSubscription = {
      event: dataForm.event,
      type: dataForm.type,
      method: dataForm.method,
      cc_emails: emailToArray(dataForm.cc_emails),
    };

    if (props.data?.id) data.id = props.data?.id;

    props.onSave(data);
  }

  const checkCCEmailsFormat = (cc_emails: string): boolean => {
    let emailsArray = emailToArray(cc_emails);
    const emailsCount = emailsArray.length;
    emailsArray = emailsArray.filter((email) => hasEmailFormat(email));
    if (emailsCount !== emailsArray.length) {
      return false;
    }
    return true;
  };

  const checkCCEmailsDuplicates = (cc_emails: string): boolean => {
    const emailsArray = emailToArray(cc_emails);
    const duplicates: string[] = emailsArray.filter(
      (item: string, index: number) => emailsArray.indexOf(item) !== index,
    );
    if (duplicates.length) {
      return false;
    }
    return true;
  };

  /// //////////////////////////////////////////////////////////////////
  /// USEEFFECT
  /// //////////////////////////////////////////////////////////////////

  useEffect(() => {
    if (eventWatch === 'Profile') {
      const auxTypesOptions = getOptions(
        'dealer_contact_notifications_profile_types',
      );
      setTypesOption(auxTypesOptions);
      let auxMethodOptions = getOptions('dealer_contact_notifications_methods');
      auxMethodOptions = auxMethodOptions.filter(
        (option: any) => option.value !== 'call_cellphone' && option.value !== 'call_workphone',
      );
      setMethodOption(auxMethodOptions);
      if (selectorChange !== undefined) {
        if (selectorChange !== 'eventProfile') {
          setValue('type', '');
          setValue('method', []);
          setSelectorChange('eventProfile');
        }
      } else {
        setSelectorChange('eventProfile');
      }
    } else if (eventWatch === 'commercial_fleet_ordering') {
      const auxTypesOptions = getOptions(
        'dealer_contact_notifications_commercial_fleet_ordering_types',
      );
      setTypesOption(auxTypesOptions);
      let auxMethodOptions = getOptions('dealer_contact_notifications_methods');
      auxMethodOptions = auxMethodOptions.filter(
        (option: any) => option.value !== 'call_cellphone' && option.value !== 'call_workphone',
      );
      setMethodOption(auxMethodOptions);
      if (selectorChange !== undefined) {
        if (selectorChange !== 'eventCommercial') {
          setValue('type', '');
          setValue('method', []);
          setSelectorChange('eventCommercial');
        }
      } else {
        setSelectorChange('eventCommercial');
      }
    } else if (eventWatch === 'urban_fleet_ordering') {
      const auxTypesOptions = getOptions(
        'dealer_contact_notifications_urban_fleet_ordering_types',
      );
      setTypesOption(auxTypesOptions);
      let auxMethodOptions = getOptions('dealer_contact_notifications_methods');
      auxMethodOptions = auxMethodOptions.filter(
        (option: any) => option.value !== 'call_cellphone'
              && option.value !== 'call_workphone'
              && option.value !== 'text_cellphone',
      );
      setMethodOption(auxMethodOptions);
      if (selectorChange !== undefined) {
        if (selectorChange !== 'eventUrban') {
          setValue('type', '');
          setValue('method', []);
          setSelectorChange('eventUrban');
        }
      } else {
        setSelectorChange('eventUrban');
      }
    } else if (eventWatch === 'tire_ers') {
      const auxTypesOptions = getOptions(
        'dealer_contact_notifications_tire_ers_types',
      );
      setTypesOption(auxTypesOptions);
      let auxMethodOptions = getOptions('dealer_contact_notifications_methods');
      auxMethodOptions = auxMethodOptions.filter(
        (option: any) => option.value !== 'call_cellphone'
              && option.value !== 'call_workphone'
              && option.value !== 'text_cellphone',
      );
      setMethodOption(auxMethodOptions);
      if (selectorChange !== undefined) {
        if (selectorChange !== 'eventTire') {
          setValue('type', '');
          setValue('method', []);
          setSelectorChange('eventTire');
        }
      } else {
        setSelectorChange('eventTire');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventWatch]);

  /// //////////////////////////////////////////////////////////////////
  /// RETURN
  /// //////////////////////////////////////////////////////////////////

  return (
    <Dialog
      onClose={() => {
        onDialogClose();
      }}
      aria-labelledby="customized-dialog-title"
      open
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <ClosableDialogTitle
        onClose={() => {
          onDialogClose();
        }}
      >
        {title}
      </ClosableDialogTitle>

      <ClosableDialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} style={{ marginTop: 7 }}>
            <Controller
              name="event"
              control={control}
              rules={{ required: true }}
              render={(item) => (
                <ListingFilterSelect
                  id="event"
                  value={item.field.value}
                  variant="standard"
                  title={t('Event')}
                  options={eventsOptions}
                  readOnly={false}
                  onChange={(e) => item.field.onChange(e.target.value)}
                  error={!!item.formState.errors.event}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: 7 }}>
            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={(item) => (
                <ListingFilterSelect
                  id="type"
                  value={item.field.value}
                  variant="standard"
                  title={t('Notification Type')}
                  options={typeOptions}
                  readOnly={!eventWatch}
                  onChange={(e) => item.field.onChange(e.target.value)}
                  error={!!item.formState.errors.type}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: 7 }}>
            <Controller
              name="method"
              control={control}
              rules={{ required: true }}
              render={(item) => (
                <DealerAutocomplete
                  id="tire_profiles"
                  variant="standard"
                  multiple
                  disableClearable
                  suggestions={methodOptions}
                  placeholder={t('Select notification methods')}
                  label={t('Method')}
                  values={item.field.value}
                  disableCloseOnSelect
                  onChange={(v: any) => item.field.onChange(v)}
                  error={!!item.formState.errors.method}
                  readOnly={!typeWatch}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} style={{ marginTop: 7 }}>
            <Grid container spacing={1} alignItems="flex-end">
              <Grid item xs={1}>
                <Email />
              </Grid>
              <Grid item xs={11}>
                <Controller
                  name="cc_emails"
                  control={control}
                  rules={{
                    validate: {
                      format: checkCCEmailsFormat,
                      duplicates: checkCCEmailsDuplicates,
                    },
                  }}
                  render={(item) => (
                    <TextField
                      variant="standard"
                      readOnly={methodWatch.length === 0}
                      style={{ display: 'flex', flexWrap: 'wrap' }}
                      label={t('CC Emails')}
                      value={item.field.value}
                      onChange={(e) => item.field.onChange(e.target.value)}
                      placeholder={t('CC Emails')}
                      error={!!item.formState.errors.cc_emails}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </ClosableDialogContent>
      <DialogActions>
        <Button
          color="primary"
          style={{ margin: '8px' }}
          onClick={handleSubmit(saveNotificationSubscription, onError)}
        >
          {props.action === 'edit' ? t('Update') : t('Add')}
        </Button>
        <Button
          color="danger"
          style={{ margin: '8px' }}
          onClick={props.onClose}
        >
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { NotificationSubscriptionsDetail };
